import axios from 'axios';
import { refreshToken, logout } from './auth'; // Import refreshToken and logout

// Default to the current host + /api if environment variable is not set
const apiUrl = process.env.REACT_APP_API_URL || `${window.location.origin}/api`;
console.log('Using API URL:', apiUrl);

const instance = axios.create({
    baseURL: apiUrl,
    headers: {
        'Content-Type': 'application/json'
    }
});

// Add request interceptor to add token and prevent caching
instance.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        // Add timestamp to URL to prevent caching
        const timestamp = new Date().getTime();
        const separator = config.url?.includes('?') ? '&' : '?';
        config.url = `${config.url}${separator}_t=${timestamp}`;

        // Force fresh responses
        config.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
        config.headers['Pragma'] = 'no-cache';
        config.headers['Expires'] = '0';
        
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Add response interceptor to log responses and handle token expiration
instance.interceptors.response.use(
    (response) => {
        const url = response.config.url?.split('?')[0]; // Remove query params from logged URL
        console.log(`API Response [${url}]:`, {
            status: response.status,
            headers: response.headers,
            data: response.data
        });
        return response;
    },
    async (error) => {
        const url = error.config?.url?.split('?')[0]; // Remove query params from logged URL
        console.error(`API Error [${url}]:`, {
            status: error.response?.status,
            data: error.response?.data,
            headers: error.response?.headers,
            error: error.message
        });

        // Handle token expiration (HTTP 403 Forbidden)
        const originalRequest = error.config;
        
        // If the request fails due to token expiration and it's not already a retry
        if (error.response?.status === 403 && !originalRequest._retry &&
            // Skip token refresh for login and token endpoints
            !originalRequest.url.includes('/api/login') && 
            !originalRequest.url.includes('/api/token')) {
            
            console.log('Token expired. Attempting to refresh...');
            originalRequest._retry = true;
            
            try {
                // Attempt to refresh the token
                const refreshSuccessful = await refreshToken();
                
                if (refreshSuccessful) {
                    // Update the authorization header with the new token
                    const newToken = localStorage.getItem('token');
                    if (newToken) {
                        originalRequest.headers.Authorization = `Bearer ${newToken}`;
                        return instance(originalRequest); // Retry with the new token
                    }
                }
                
                // If refresh failed or no new token was obtained
                console.error('Token refresh failed. Redirecting to login...');
                logout(); // Use the centralized logout function
            } catch (refreshError) {
                console.error('Error during token refresh:', refreshError);
                logout(); // Use the centralized logout function
            }
        }
        
        return Promise.reject(error);
    }
);

export default instance;